import { GameFrame } from '@mogzo/play';
import type { LaunchParams } from '@mogzo/shared';
import { type RefObject, useEffect, useRef, useState } from 'react';
import type { PlayableBuild } from '../../lib/communities';
import { useScreenSize } from './launch';

export function PlayIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M7 4.5v15l12-7.5z" />
    </svg>
  );
}

/** Whether the element is on screen or close to it (always, where the browser can't tell). */
function useNearScreen(element: RefObject<HTMLElement | null>): boolean {
  const [near, setNear] = useState(typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const target = element.current;
    if (!target || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => setNear(entries[entries.length - 1]?.isIntersecting ?? false),
      { rootMargin: '200px 0px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [element]);
  return near;
}

/** MOGZO's splash for games without their own: the name and a Play button. */
function DefaultSplash({
  name,
  developerName,
  onPlay,
}: {
  name: string;
  developerName: string;
  onPlay: () => void;
}) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-ink-soft bg-[radial-gradient(rgba(255,203,47,0.16)_1.5px,transparent_1.5px)] bg-size-[22px_22px] p-6 text-center text-white">
      <span className="flex size-16 items-center justify-center rounded-[18px] bg-brand font-display text-3xl text-ink">
        {name.slice(0, 1).toUpperCase()}
      </span>
      <div>
        <p className="text-xl font-bold sm:text-2xl">{name}</p>
        <p className="text-sm text-white/70">by {developerName}</p>
      </div>
      <button
        type="button"
        onClick={onPlay}
        className="mt-1 inline-flex min-h-12 items-center gap-2.5 rounded-full bg-brand px-7 text-base font-bold text-ink hover:bg-brand-hover"
      >
        <PlayIcon />
        Play
      </button>
    </div>
  );
}

/**
 * A build in a post: the game's own splash page (M18c) or MOGZO's default. The splash runs only
 * while the post is near the screen; its Play (`mogzo.launch()`) calls `onLaunch`.
 */
export function SplashCard({
  name,
  developerName,
  build,
  projectId,
  onLaunch,
}: {
  name: string;
  developerName: string;
  build: PlayableBuild;
  projectId: string;
  onLaunch: (params: LaunchParams) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const near = useNearScreen(box);
  const [shown, setShown] = useState(false);
  // A panel the splash opens covers the post on a computer or tablet, and the feed on a phone.
  const size = useScreenSize();

  useEffect(() => {
    if (!near) setShown(false);
  }, [near]);

  return (
    <div
      ref={box}
      className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl bg-[#100c24] sm:aspect-video"
    >
      <DefaultSplash name={name} developerName={developerName} onPlay={() => onLaunch({})} />
      {build.splashUrl && near && (
        <div
          className={`absolute inset-0 transition-opacity duration-300 ${shown ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        >
          <GameFrame
            url={build.splashUrl}
            title={`${name} splash`}
            projectId={projectId}
            view="splash"
            onLaunch={onLaunch}
            onLoad={() => setShown(true)}
            panels={{ device: size, gameName: name, overPage: size === 'phone' }}
          />
        </div>
      )}
    </div>
  );
}
